import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import CasePicker, { CaseSelection } from '../CasePicker/CasePicker';
import CourtEventApi, { CourtEventAttending } from '../../api/CourtEventApi';
import AttendingControl from '../AttendingControl/AttendingControl';
import {
  courtEventFormProblem,
  courtWallClockToMillis,
  defaultEnd,
} from '../../utils/courtEventForm';

/**
 * Enter a court date by hand, from the calendar (19 Sep meeting).
 *
 * ## Why it exists
 *
 * The calendar fills from uploaded PDF notices, and a good share of court dates never arrive as
 * one. Danny, 19 Sep: *"sometimes we get court dates via email, and we have to have a way to enter
 * them by hand. We just, we have to."* The worst case is a county court emailing a docket listing
 * thirty to fifty defendants, one of whom is a client — nothing about that is uploadable.
 *
 * Kelsi put the consequence plainly in the same meeting: *"Our goal as Calendar Team is to schedule
 * and make sure we don't miss courts, and if I can't add a court date, then obviously we're gonna be
 * missing it."*
 *
 * ## Why here rather than on the case screen
 *
 * Adding a court event already worked, from the client screen and the case screen. Both open from a
 * case, so neither is reachable by somebody working a docket email in front of the calendar: they
 * would have to find the client first, on another screen, for every line. Lloyd asked in the meeting
 * for *"a different button on the side that you can click on, add the information that you want to,
 * and then that would insert that there"*, and this is that button.
 *
 * The case therefore has to be chosen rather than assumed, which is the only part that is new.
 * `CasePicker` was built for the same problem on the bulk-notice screen and carries the court on
 * each option, because a citation number matches on roughly one notice in five and two clients can
 * share a name.
 *
 * ## ⚠️ What it does not do
 *
 * No reminder is scheduled and no client is notified. The meeting discussed both alongside this
 * button; they are a separate item and they wait on the texting channel. Saying so here because a
 * date that silently notifies nobody is the kind of thing a calendar team would reasonably assume
 * had happened.
 *
 * It also does not reach Outlook. Creating a court event attempts a Microsoft Graph calendar event,
 * that scope has never been granted, and the failure is swallowed by design so the court date still
 * saves. Already true of every court event, not something this introduces.
 */

interface AddCourtDateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save, so the calendar can reload the visible range. */
  onCreated: () => void;
}

interface DateTimeForm {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  title: string;
  description: string;
  isTrialSetting: boolean;
}

const EMPTY: DateTimeForm = {
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  title: '',
  description: '',
  isTrialSetting: false,
};

/** The case is this dialog's own question; the rest of the rules are shared with the other two. */
function formProblem(form: DateTimeForm, selected: CaseSelection | null): string | null {
  if (!selected) return 'Choose which case this court date belongs to.';
  return courtEventFormProblem(form);
}

const AddCourtDateDialog: React.FC<AddCourtDateDialogProps> = ({ open, onClose, onCreated }) => {
  const [selected, setSelected] = useState<CaseSelection | null>(null);
  const [form, setForm] = useState<DateTimeForm>(EMPTY);
  const [attending, setAttending] = useState<CourtEventAttending>('NOT_SET');
  const [coveringAttorneyGuid, setCoveringAttorneyGuid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem = formProblem(form, selected);

  const reset = () => {
    setSelected(null);
    setForm(EMPTY);
    setAttending('NOT_SET');
    setCoveringAttorneyGuid(null);
    setError(null);
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const set = (field: keyof DateTimeForm) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const onStartDateChange = (value: string) =>
    setForm((prev) => ({ ...prev, startDate: value, endDate: prev.endDate || value }));

  /** Suggest an end only while the person has not set one, and never overwrite what they typed. */
  const onStartTimeChange = (value: string) =>
    setForm((prev) => {
      if (prev.endTime) return { ...prev, startTime: value };
      const suggested = defaultEnd(prev.startDate || value, value);
      return suggested
        ? { ...prev, startTime: value, endDate: suggested.endDate, endTime: suggested.endTime }
        : { ...prev, startTime: value };
    });

  const save = async () => {
    if (problem || !selected) return;
    setSaving(true);
    setError(null);
    try {
      const start = courtWallClockToMillis(form.startDate, form.startTime);
      const end = courtWallClockToMillis(form.endDate, form.endTime);
      if (start === null || end === null) {
        setError('Check the date and time.');
        return;
      }

      await CourtEventApi.createCourtEvent({
        Ticket_guid: selected.ticketGuid,
        CourtEvent_Start: start,
        CourtEvent_End: end,
        CourtEvent_Title: form.title.trim(),
        CourtEvent_Desc: form.description.trim(),
        /*
         * The one moment a person is typing "Jury Trial" by hand. Nothing else on a court event
         * distinguishes the trial from a pretrial or a reset, and the flag is set on none of the
         * upcoming settings, which is why the calendar has to guess a trial from its title.
         */
        CourtEvent_IsTrialSetting: form.isTrialSetting,
        CourtEvent_Attending: attending,
        CourtEvent_CoveringAttorney_guid: coveringAttorneyGuid,
      });
      reset();
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the court date');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Add a court date</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <CasePicker
            label="Case"
            selectedLabel={
              selected
                ? `${selected.ticketNumber} — ${selected.clientName}${
                    selected.courtName ? ` — ${selected.courtName}` : ' — no court on record'
                  }`
                : null
            }
            onSelect={setSelected}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
            <TextField
              fullWidth
              label="Start time"
              type="time"
              value={form.startTime}
              onChange={(e) => onStartTimeChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="End date"
              type="date"
              value={form.endDate}
              onChange={(e) => set('endDate')(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
            <TextField
              fullWidth
              label="End time"
              type="time"
              value={form.endTime}
              onChange={(e) => set('endTime')(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
          </Box>

          <TextField
            fullWidth
            label="What the setting is"
            placeholder="Pretrial, docket call, jury trial"
            value={form.title}
            onChange={(e) => set('title')(e.target.value)}
            disabled={saving}
          />

          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => set('description')(e.target.value)}
            disabled={saving}
          />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Who is attending from the firm?
            </Typography>
            <AttendingControl
              value={attending}
              coveringAttorneyGuid={coveringAttorneyGuid}
              autoSave={false}
              disabled={saving}
              onChanged={(next, _, attorneyGuid) => {
                setAttending(next);
                setCoveringAttorneyGuid(attorneyGuid);
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.isTrialSetting}
                onChange={(e) => setForm((prev) => ({ ...prev, isTrialSetting: e.target.checked }))}
                disabled={saving}
              />
            }
            label="This setting is the trial"
          />

          {/* Named, so a disabled Save button is never a mystery. */}
          {problem && (
            <Typography variant="caption" color="text.secondary">
              {problem}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || problem !== null}>
          {saving ? 'Adding…' : 'Add court date'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourtDateDialog;
